import "../../styles/global.scss";
import { Container, Row, Col, Form, Button, Alert, Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import happyKids from "../../images/carerchild.png";
import logo from "../../images/logo.png";
import styles from "../../styles/login.module.scss";
import axios from "axios";
import { serverUrl } from "../../helpers/utility";
import { useState, useEffect } from "react";
import { mutate } from "swr";
import Loading from "../../components/loading";

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  // Fetch invite details on mount
  useEffect(() => {
    if (!token) {
      setError("Missing invite token");
      setLoading(false);
      return;
    }

    axios
      .get(`${serverUrl}/invite/accept`, { params: { token } })
      .then((response) => {
        if (response.data?.success && response.data?.invite) {
          setInviteData(response.data.invite);
          setValue("username", response.data.invite.email);
        } else {
          setError(response.data?.message || "Invalid invite");
        }
      })
      .catch((err) => {
        console.error("Error fetching invite:", err);
        setError(err.response?.data?.message || "Invalid or expired invite");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, setValue]);


  const onSubmit = async (data) => {
    if (!token) {
      setSubmitError("Missing invite token");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // New user registration with invite token
      const response = await axios.post(`${serverUrl}/user/register/email`, {
        user: {
          email: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
        },
        inviteToken: token,
      });

      if (response.data?.success) {
        const { user, token: authToken } = response.data;
        localStorage.setItem("token", authToken);
        await mutate(`${serverUrl}/user`);
        await mutate(`${serverUrl}/location`);
        navigate("/local-authority/dashboard");
      } else {
        setSubmitError(response.data?.message || "Failed to accept invite");
      }
    } catch (err) {
      console.error("Error accepting invite:", err);
      const errorMessage = err.response?.data?.message || "Failed to accept invite. Please try again.";
      setSubmitError(errorMessage);
      
      // If user already exists, suggest they login
      if (errorMessage.includes("already exists") || errorMessage.includes("User already")) {
        setSubmitError(
          "An account with this email already exists. Please login to your account first, then contact your Local Authority administrator to resend the invite."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className={`no-padding no-margin container-fluid`}>
        <Row>
          <Col lg={12} className={styles.loginArea}>
            <Loading />
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className={`no-padding no-margin container-fluid`}>
        <Row>
          <Col lg={6} xl={7} className={styles.loginMarketing} style={{ backgroundImage: `url(${happyKids})` }}></Col>
          <Col lg={6} xl={5} className={`${styles.loginArea}`}>
            <div style={{ padding: "40px" }}>
              <img src={logo} alt="bPlaced" style={{ width: "150px", marginBottom: "30px" }} />
              <Alert variant="danger">
                <h5>Invalid Invite</h5>
                <p>{error}</p>
                <p>The invite link may have expired or is invalid.</p>
              </Alert>
              <Button variant="primary" onClick={() => navigate("/login")} className="mt-3">
                Go to Login
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }


  return (
    <Container fluid className={`no-padding no-margin container-fluid`}>
      <Row>
        <Col lg={6} xl={7} className={styles.loginMarketing} style={{ backgroundImage: `url(${happyKids})` }}></Col>
        <Col lg={6} xl={5} className={`${styles.loginArea}`}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={{ padding: "40px" }}>
              <img src={logo} alt="bPlaced" style={{ width: "150px", marginBottom: "30px" }} />
              
              <h3 className="mb-3">Accept Invite</h3>
              
              {inviteData && (
                <Alert variant="info" className="mb-4">
                  <p className="mb-1">
                    <strong>You've been invited to join as an Admin</strong>
                  </p>
                  <p className="mb-0" style={{ fontSize: "14px" }}>
                    Email: <strong>{inviteData.email}</strong>
                  </p>
                </Alert>
              )}

              {submitError && (
                <Alert variant="danger" className="mb-3">
                  {submitError}
                </Alert>
              )}

              <div className="mb-3">
                <label htmlFor="firstName" className="form-label">
                  First name
                </label>
                <input
                  {...register("firstName", { required: "First name is required" })}
                  className="form-control"
                  type="text"
                />
                {errors.firstName && (
                  <div className="text-danger" style={{ fontSize: "12px" }}>
                    {errors.firstName.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="lastName" className="form-label">
                  Last name
                </label>
                <input
                  {...register("lastName", { required: "Last name is required" })}
                  className="form-control"
                  type="text"
                />
                {errors.lastName && (
                  <div className="text-danger" style={{ fontSize: "12px" }}>
                    {errors.lastName.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="username" className="form-label">
                  Email address
                </label>
                <input
                  {...register("username", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  className="form-control"
                  type="email"
                  disabled
                />
                <small className="text-muted">This email is pre-filled from your invite</small>
                {errors.username && (
                  <div className="text-danger" style={{ fontSize: "12px" }}>
                    {errors.username.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  className="form-control"
                  type="password"
                />
                {errors.password && (
                  <div className="text-danger" style={{ fontSize: "12px" }}>
                    {errors.password.message}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-100 mb-3"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating Account..." : "Accept Invite & Create Account"}
              </Button>

              <div className="text-center">
                <small>
                  Already have an account? <a href="#/login">Login here</a>
                </small>
              </div>
            </div>
          </form>
        </Col>
      </Row>
    </Container>
  );
}

export default AcceptInvite;

